
import React, { useState, useEffect } from 'react';
import { Crown, X, CreditCard, Clock, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getActivePriceDisplay, FOUNDERS_PRICING } from '../stripe-config';
import { usePayment } from '../hooks/usePayment';
import { supabase } from '../lib/supabase';

interface TrialBannerProps {
  daysLeft: number;
  onDismiss: () => void;
}

export function TrialBanner({ daysLeft, onDismiss }: TrialBannerProps) {
  const navigate = useNavigate();
  const pricing = getActivePriceDisplay();
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    if (!pricing.isFounders) return;

    const tick = () => {
      const diff = FOUNDERS_PRICING.endDate.getTime() - Date.now();
      if (diff <= 0) { setCountdown(''); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setCountdown(`${d}d ${h}h ${m}m`);
    };

    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [pricing.isFounders]);

  const { handlePayment, loading: paymentLoading } = usePayment();

  const handleUpgradeClick = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const result = await handlePayment({ interval: 'monthly', email: user?.email || '' });
      if (result?.url) window.location.href = result.url;
    } catch {
      navigate('/dashboard/settings', { state: { activeTab: 'subscription' } });
    }
  };

  const getUrgencyColor = () => {
    if (daysLeft <= 3) return 'from-red-500 to-red-600';
    if (daysLeft <= 7) return 'from-yellow-500 to-orange-500';
    return 'from-electric to-blue-600';
  };

  const getUrgencyText = () => {
    if (daysLeft === 1) return 'Trial ends tomorrow!';
    if (daysLeft <= 3) return 'Trial ending soon!';
    return `${daysLeft} days left in trial`;
  };

  return (
    <div className={`bg-gradient-to-r ${getUrgencyColor()} text-white p-4 rounded-lg shadow-lg mb-6 relative`}>
      <button
        onClick={onDismiss}
        className="absolute top-2 right-2 text-white/80 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-center justify-between pr-8">
        <div className="flex items-center">
          <Crown className="h-5 w-5 mr-3 flex-shrink-0" />
          <div>
            <div className="font-semibold text-sm md:text-base">
              {getUrgencyText()}
            </div>
            <div className="text-xs md:text-sm text-white/90">
              {pricing.isFounders
                ? <>Lock in Founders Pricing — limited time offer!{countdown ? <span className="ml-1 font-mono opacity-80">({countdown} left)</span> : null}</>
                : 'Upgrade to continue using all premium features'}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className="hidden md:flex items-center text-xs text-white/90">
            {pricing.isFounders ? (
              <>
                <Sparkles className="h-3 w-3 mr-1" />
                <span className="line-through opacity-70 mr-1">${pricing.regularMonthly}</span> ${pricing.monthly}/month
              </>
            ) : (
              <>
                <Clock className="h-3 w-3 mr-1" />
                From ${pricing.monthly}/month
              </>
            )}
          </div>
          <button
            onClick={handleUpgradeClick}
            disabled={paymentLoading}
            className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center whitespace-nowrap disabled:opacity-60"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {paymentLoading ? 'Loading…' : 'Upgrade Now'}
          </button>
        </div>
      </div>
    </div>
  );
}
